import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { fetchWithCSRF } from "../../../admin/common/helpers";
import { useModal } from "../../../admin/react/hooks/use-modal";
import { AltTextModal } from "./alt_text_modal.jsx";

// Copy is verbatim from EVNT-4046 and must not vary across surfaces.
const GENERATION_FAILURE =
  "We weren't able to generate alternative text. Please try again.";
const RATE_LIMIT =
  "You've reached the limit for alternative text generation. Please try again in an hour.";

const CHAR_LIMIT = 125;

const GA_ACTION_GENERATE = "Generate Image Alternative Text";
const GA_ACTION_ACCEPT = "Accept AI Generated Photo Alternative Text";
const GA_ACTION_DISMISS = "Dismiss AI Generated Photo Alternative Text";

const trackAltTextEvent = (category, action, label) => {
  if (!category) return;
  const event = {
    category,
    action,
    providers: ["google-analytics"],
  };
  if (label !== null && label !== undefined) event.label = label;
  window.Slzr?.AnalyticsQueue?.push(event);
};

/**
 * AI "Generate Alt Text" trigger, rendered next to a photo caption field inside
 * PhotoChooser's Photo (photo-selected) mode. Self-gates on `enabled` so
 * surfaces that don't opt in render nothing and stay backward-compatible.
 *
 * The button opens a dedicated AltTextModal and immediately kicks off
 * generation; the modal owns the review/edit UI. This component owns the
 * generation lifecycle (fetch, abort, state) and wires results into the modal.
 */
export const AltTextGenerator = ({
  enabled,
  analyticsCategory,
  triggerBtnClass,
  fieldClassName,
  modalBtnClasses,
  photoKey,
  generateUrl,
  imageUrl,
  captionInputId,
  getCaption,
  onAccept,
}) => {
  const [isOpen, openModal, closeModal] = useModal(false);
  // "loading" | "preview" | "error" | "rateLimited"
  const [status, setStatus] = useState("loading");
  const [suggestion, setSuggestion] = useState("");
  const [error, setError] = useState("");

  const abortControllerRef = useRef(null);
  const generateButtonRef = useRef(null);

  // Abort any in-flight request on unmount.
  useEffect(
    () => () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    [],
  );

  const runGeneration = useCallback(async ({ skipCache = false, previousSuggestion = null } = {}) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setStatus("loading");
    setError("");

    try {
      const payload = {
        photo_key: photoKey,
        existing_caption: getCaption(),
      };
      if (skipCache) {
        payload.skip_cache = true;
      }
      if (previousSuggestion) {
        payload.previous_suggestion = previousSuggestion;
      }

      const response = await fetchWithCSRF(generateUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: abortControllerRef.current.signal,
      });

      // Rate limiting is enforced server-side by EVNT-4082; surface it here now.
      if (response.status === 429) {
        setError(RATE_LIMIT);
        setStatus("rateLimited");
        return;
      }

      const contentType = response.headers.get("content-type");
      const data =
        contentType && contentType.includes("application/json")
          ? await response.json()
          : null;

      if (data && data.success && data.alt_text) {
        setSuggestion(data.alt_text.slice(0, CHAR_LIMIT));
        setStatus("preview");
        return;
      }

      setError(GENERATION_FAILURE);
      setStatus("error");
    } catch (err) {
      if (err.name === "AbortError") {
        return;
      }
      setError(GENERATION_FAILURE);
      setStatus("error");
    }
  }, [generateUrl, photoKey, getCaption]);

  const resetState = useCallback(() => {
    setSuggestion("");
    setError("");
    setStatus("loading");
  }, []);

  const handleRegenerate = useCallback(() => {
    runGeneration({ skipCache: true, previousSuggestion: suggestion });
  }, [runGeneration, suggestion]);

  const handleOpen = useCallback(() => {
    const caption = getCaption();
    const label = caption?.trim() ? "Has Existing Caption" : "No Existing Caption";
    trackAltTextEvent(analyticsCategory, GA_ACTION_GENERATE, label);
    resetState();
    openModal(runGeneration);
  }, [openModal, runGeneration, resetState, analyticsCategory, getCaption]);

  const handleClose = useCallback(() => {
    if (status === "preview") {
      trackAltTextEvent(analyticsCategory, GA_ACTION_DISMISS);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    resetState();
    // Return focus to the trigger for keyboard users (WCAG 2.4.3), after the
    // dialog has closed so the native focus-return doesn't clobber it.
    closeModal(() => {
      if (generateButtonRef.current) {
        generateButtonRef.current.focus();
      }
    });
  }, [closeModal, resetState, status, analyticsCategory]);

  const handleAccept = useCallback(() => {
    trackAltTextEvent(analyticsCategory, GA_ACTION_ACCEPT);
    onAccept(suggestion);
    resetState();
    closeModal(() => {
      const captionEl =
        captionInputId && document.getElementById(captionInputId);
      if (captionEl) {
        captionEl.focus();
      }
    });
  }, [onAccept, suggestion, captionInputId, closeModal, resetState, analyticsCategory]);

  if (!enabled) {
    return null;
  }

  return (
    <div className="photo-chooser--alt-text">
      <button
        type="button"
        ref={generateButtonRef}
        className={classNames("photo-chooser--alt-text-generate", triggerBtnClass)}
        onClick={handleOpen}
      >
        <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />
        Generate Alt Text
      </button>

      <AltTextModal
        isOpen={isOpen}
        imageUrl={imageUrl}
        status={status}
        suggestion={suggestion}
        error={error}
        charLimit={CHAR_LIMIT}
        fieldClassName={fieldClassName}
        modalBtnClasses={modalBtnClasses}
        onSuggestionChange={setSuggestion}
        onRegenerate={handleRegenerate}
        onAccept={handleAccept}
        onClose={handleClose}
      />
    </div>
  );
};

AltTextGenerator.propTypes = {
  // Feature flag gate; when false the component renders nothing.
  enabled: PropTypes.bool,
  // GA4 event category for this surface (e.g. "Admin Event", "Public Event").
  // When absent, no analytics events are pushed.
  analyticsCategory: PropTypes.string,
  // Extra CSS classes for the trigger button (theme-specific, e.g.
  // "admin-secondary-btn" or "em-button em-secondary").
  triggerBtnClass: PropTypes.string,
  // Extra CSS class(es) for the modal's textarea field wrapper
  // (e.g. "em-basic-input" to inherit emphasis form field styles).
  fieldClassName: PropTypes.string,
  // Theme-specific CSS classes for the modal's action buttons.
  modalBtnClasses: PropTypes.shape({
    ok: PropTypes.string,
    cancel: PropTypes.string,
    tertiary: PropTypes.string,
  }),
  // Identifies the photo to the endpoint ("id:NN" for library, else temp token).
  photoKey: PropTypes.string,
  // AI alt-text endpoint (POST).
  generateUrl: PropTypes.string,
  // Image shown in the modal (photo.preview_url).
  imageUrl: PropTypes.string,
  // id of the caption input, used to return focus on Accept.
  captionInputId: PropTypes.string,
  // Reads the caption field's current value at request time.
  getCaption: PropTypes.func.isRequired,
  // Writes the accepted (possibly edited) suggestion into the caption field.
  onAccept: PropTypes.func.isRequired,
};

AltTextGenerator.defaultProps = {
  enabled: false,
  analyticsCategory: null,
  triggerBtnClass: null,
  fieldClassName: null,
  modalBtnClasses: {},
  photoKey: null,
  generateUrl: "/photos/generate_alt_text",
  imageUrl: null,
  captionInputId: undefined,
};
